import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { usersApi } from '../../services/usersApi';
import { AlertTriangle } from 'lucide-react';
import './DeleteUserModal.css';

export function DeleteUserModal({ isOpen, onClose, onSuccess, user }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const handleDelete = async () => {
    if (!confirmed) {
      setError(t('users.modals.delete.confirmRequired'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await usersApi.delete(user.id);
      onSuccess();
      onClose();
      setConfirmed(false);
    } catch (err) {
      setError(err.message || t('users.modals.delete.errors.failedToDelete'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setConfirmed(false);
    setError('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="sm">
      <div className="delete-user-modal">
        <div className="delete-modal-header">
          <div className="delete-icon">
            <AlertTriangle size={48} />
          </div>
          <h2 className="delete-modal-title">{t('users.modals.delete.title')}</h2>
        </div>

        <div className="delete-modal-body">
          <p className="delete-message">
            {t('users.modals.delete.confirmUserMessage', {
              name: `${user.firstName} ${user.lastName}`,
              email: user.email,
            })}
          </p>
          <p className="delete-warning">
            {t('users.modals.delete.message')}
          </p>

          <div className="confirm-checkbox">
            <label className="form-checkbox-label">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => {
                  setConfirmed(e.target.checked);
                  setError('');
                }}
                disabled={loading}
              />
              <span>{t('users.modals.delete.confirmCheckbox')}</span>
            </label>
          </div>

          {error && (
            <div className="form-error" style={{ marginTop: '12px' }}>{error}</div>
          )}
        </div>

        <div className="modal-footer">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {t('users.modals.delete.cancel')}
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleDelete}
            disabled={!confirmed || loading}
          >
            {loading ? t('common.deleting') : t('users.modals.delete.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
